import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, money, fmtDate, errMsg } from "@/lib/api";
import { useStore } from "@/contexts/StoreContext";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import {
  ChevronLeft, PackageCheck, ShieldCheck, ClipboardList, Clock, MapPin, Home, Phone,
  Package, Truck, CheckCircle2, ShoppingBag, PiggyBank, Tag, Receipt, Bike,
  Printer, LifeBuoy, MessageCircle, Copy, AlertTriangle, Timer, Sparkles, Wallet,
} from "lucide-react";
import { toast } from "sonner";

// Canonical 5-step tracker mapped onto the backend order state machine.
// Every backend status collapses onto the nearest visual step (no data changes).
const TRACK_STEPS = [
  { key: "placed",           label: "Order placed",     Icon: ClipboardList, matches: ["pending"] },
  { key: "confirmed",        label: "Order confirmed",  Icon: CheckCircle2,  matches: ["confirmed"] },
  { key: "packing",          label: "Packing",          Icon: Package,       matches: ["packed", "ready"] },
  { key: "out_for_delivery", label: "Out for delivery", Icon: Bike,          matches: ["assigned", "out_for_delivery"] },
  { key: "delivered",        label: "Delivered",        Icon: PackageCheck,  matches: ["delivered"] },
];

const STATUS_STYLE = {
  pending:          { label: "Pending",           cls: "bg-amber-100 text-amber-800 border-amber-200" },
  confirmed:        { label: "Confirmed",         cls: "bg-blue-100 text-blue-800 border-blue-200" },
  packed:           { label: "Packing",           cls: "bg-indigo-100 text-indigo-800 border-indigo-200" },
  ready:            { label: "Ready for pickup",  cls: "bg-indigo-100 text-indigo-800 border-indigo-200" },
  assigned:         { label: "Rider assigned",    cls: "bg-violet-100 text-violet-800 border-violet-200" },
  out_for_delivery: { label: "Out for delivery",  cls: "bg-primary/10 text-primary border-primary/20" },
  delivered:        { label: "Delivered",         cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  cancelled:        { label: "Cancelled",         cls: "bg-red-100 text-red-800 border-red-200" },
  returned:         { label: "Returned",          cls: "bg-red-100 text-red-800 border-red-200" },
  refunded:         { label: "Refunded",          cls: "bg-red-100 text-red-800 border-red-200" },
};

export default function CustomerOrderDetail() {
  const { id } = useParams();
  const { social, store, etaMinutes } = useStore();
  const [order, setOrder] = useState(null);
  const [invoice, setInvoice] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  const load = () => {
    api.get(`/orders/${id}`).then((r) => setOrder(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get(`/invoices/by-order/${id}`).then((r) => setInvoice(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sprint 58 — Auto-refresh every 15s while the order is in-flight so the customer
  // sees the delivery partner instantly after admin assignment. Stops once terminal.
  useEffect(() => {
    if (!order) return;
    if (["delivered", "cancelled", "returned", "refunded"].includes(order.status)) return;
    const t = setInterval(() => {
      api.get(`/orders/${id}`).then((r) => setOrder(r.data)).catch(() => {});
    }, 15000);
    return () => clearInterval(t);
  }, [order?.status, id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Timestamp lookup: pull the first entry that matches any of the step's backend statuses.
  const timeAt = useMemo(() => {
    if (!order?.timeline) return {};
    const map = {};
    for (const step of TRACK_STEPS) {
      const hit = order.timeline.find((t) => step.matches.includes(t.status));
      if (hit) map[step.key] = hit.at;
    }
    return map;
  }, [order]);

  const activeStepIdx = useMemo(() => {
    if (!order) return -1;
    const idx = TRACK_STEPS.findIndex((s) => s.matches.includes(order.status));
    return idx >= 0 ? idx : (order.status === "pending" ? 0 : 0);
  }, [order]);

  const isTerminal = order && ["cancelled", "returned", "refunded"].includes(order.status);
  const canCancel = order && ["pending", "confirmed"].includes(order.status);

  const cancel = async () => {
    if (!window.confirm("Cancel this order?")) return;
    setCancelling(true);
    try { await api.patch(`/orders/${order.id}/status`, { status: "cancelled" }); toast.success("Order cancelled"); load(); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setCancelling(false); }
  };

  const copyOrderNumber = async () => {
    try { await navigator.clipboard.writeText(order.order_number); toast.success("Order number copied"); }
    catch { toast.error("Copy failed"); }
  };

  if (!order) {
    return (
      <div className="max-w-4xl mx-auto px-4 md:px-6 py-8 space-y-4" data-testid="order-loading">
        <Skeleton className="h-6 w-40" />
        <Skeleton className="h-32 w-full rounded-2xl" />
        <Skeleton className="h-48 w-full rounded-2xl" />
        <div className="grid md:grid-cols-3 gap-4">
          <Skeleton className="h-56 md:col-span-2 rounded-2xl" />
          <Skeleton className="h-56 rounded-2xl" />
        </div>
      </div>
    );
  }

  const status = STATUS_STYLE[order.status] || { label: order.status?.replace(/_/g, " "), cls: "bg-secondary text-foreground border-border" };
  const p = order.pricing || {};
  const subtotal = Number(p.subtotal || 0);
  const tax = Number(p.tax || 0);
  const discount = Number(p.discount || 0);
  const wallet = Number(p.wallet_used || 0);
  const deliveryFee = Number(p.delivery_fee || 0);
  const packaging = Number(p.packaging_fee || 0);
  const grand = Number(p.total || 0);
  const totalSavings = discount + wallet;

  const whatsapp = (social?.whatsapp || "").trim();
  const supportHref = whatsapp || `mailto:hello@babubaazar.in?subject=Help%20with%20order%20${encodeURIComponent(order.order_number)}`;

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-6 py-6 md:py-8 pb-28 md:pb-10" data-testid="customer-order-detail-page">
      <Link to="/account/orders" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-4" data-testid="back-to-orders">
        <ChevronLeft className="w-4 h-4" /> Back to my orders
      </Link>

      {/* Order Status Hero Card */}
      <Card className="border-border/60 rounded-2xl overflow-hidden mb-5 slide-up-in" data-testid="order-status-hero">
        <CardContent className="p-4 md:p-5">
          <div className="flex items-start gap-4 flex-wrap">
            <div className="min-w-0 flex-1">
              <div className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider ${status.cls}`} data-testid="order-status-badge">
                <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />
                {status.label}
              </div>
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Order</span>
                <span className="text-lg md:text-2xl font-display font-semibold font-mono" data-testid="order-number">{order.order_number}</span>
                <button onClick={copyOrderNumber} className="text-muted-foreground hover:text-primary transition" title="Copy" data-testid="copy-order-number">
                  <Copy className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="text-[11px] md:text-xs text-muted-foreground mt-1 flex items-center gap-1.5">
                <Clock className="w-3 h-3" /> Placed {fmtDate(order.created_at)}
              </div>
            </div>

            <div className="text-right space-y-1 text-xs md:text-sm">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Payment</div>
              <div className="font-bold uppercase text-foreground">{order.payment?.method}</div>
              <div className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-bold uppercase ${order.payment?.status === "captured" || order.payment?.status === "paid" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                {order.payment?.status || "—"}
              </div>
              <div className="text-lg md:text-xl font-display font-semibold tabular-nums" data-testid="order-total-hero">{money(grand)}</div>
            </div>
          </div>

          {/* ETA callout — only during in-flight states */}
          {!isTerminal && order.status !== "delivered" && (
            <div className="mt-4 pt-3 border-t flex items-center gap-2.5 text-sm">
              <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary grid place-items-center shrink-0"><Timer className="w-4 h-4" /></div>
              <div className="min-w-0">
                <div className="font-semibold leading-tight">Expected within {etaMinutes || 15} minutes of confirmation</div>
                <div className="text-[11px] text-muted-foreground">Live updates below</div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Vertical Timeline */}
      {!isTerminal && (
        <Card className="border-border/60 rounded-2xl mb-5" data-testid="order-tracker">
          <CardContent className="p-4 md:p-5">
            <div className="font-display text-lg font-semibold mb-4 flex items-center gap-2">
              <Truck className="w-4 h-4 text-primary" /> Track your order
            </div>
            <ol className="relative pl-2">
              {TRACK_STEPS.map((step, i) => {
                const done = i < activeStepIdx || (i === activeStepIdx && order.status === "delivered");
                const active = i === activeStepIdx && !done;
                const upcoming = i > activeStepIdx;
                const at = timeAt[step.key];
                const iconCls = done
                  ? "bg-emerald-500 text-white border-emerald-500"
                  : active
                    ? "bg-primary text-primary-foreground border-primary ring-4 ring-primary/20"
                    : "bg-secondary text-muted-foreground border-border";
                const lineCls = done ? "bg-emerald-500" : "bg-border";
                const isLast = i === TRACK_STEPS.length - 1;
                return (
                  <li key={step.key} className="flex gap-3 items-start pb-4 last:pb-0 relative" data-testid={`step-${step.key}-${done ? "done" : active ? "active" : "pending"}`}>
                    {!isLast && (
                      <span className={`absolute left-[19px] top-10 bottom-0 w-[2px] ${lineCls} transition-colors duration-500`} aria-hidden />
                    )}
                    <div className={`w-10 h-10 rounded-full grid place-items-center border-2 shrink-0 z-10 transition ${iconCls}`}>
                      <step.Icon className="w-4 h-4" />
                    </div>
                    <div className="pt-1.5 flex-1 min-w-0">
                      <div className={`text-sm font-semibold leading-tight ${upcoming ? "text-muted-foreground" : "text-foreground"}`}>{step.label}</div>
                      {at && <div className="text-[11px] text-muted-foreground mt-0.5">{fmtDate(at)}</div>}
                      {active && !at && <div className="text-[11px] text-primary font-medium mt-0.5">In progress…</div>}
                    </div>
                  </li>
                );
              })}
            </ol>
          </CardContent>
        </Card>
      )}

      <div className="grid md:grid-cols-3 gap-4 md:gap-5">
        {/* Items + Bill */}
        <Card className="border-border/60 rounded-2xl md:col-span-2" data-testid="order-items-card">
          <CardContent className="p-4 md:p-5">
            <div className="font-display text-lg font-semibold mb-3 flex items-center gap-2">
              <ShoppingBag className="w-4 h-4 text-primary" /> Items ({(order.items || []).length})
            </div>
            <div className="divide-y" data-testid="order-items-list">
              {(order.items || []).map((it, i) => (
                <div key={i} className="py-3 flex items-center gap-3">
                  <div className="w-14 h-14 rounded-xl border border-border/60 bg-white overflow-hidden shrink-0 grid place-items-center p-1">
                    {it.image
                      ? <img src={it.image} alt="" className="w-full h-full object-contain" loading="lazy" />
                      : <Package className="w-5 h-5 text-primary/40" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">{it.name}</div>
                    <div className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-1.5">
                      <span>Qty {it.qty}</span>
                      <span>·</span>
                      <span className="tabular-nums">{money(it.price_snapshot)} each</span>
                    </div>
                  </div>
                  <div className="text-sm font-bold tabular-nums shrink-0" data-testid={`order-item-total-${i}`}>{money(Number(it.price_snapshot) * Number(it.qty))}</div>
                </div>
              ))}
            </div>

            {totalSavings > 0 && (
              <div className="mt-4 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 px-3 py-2 flex items-center gap-2 text-[13px]" data-testid="order-savings">
                <Sparkles className="w-4 h-4 shrink-0" />
                <span className="font-semibold">You saved {money(totalSavings)} on this order</span>
              </div>
            )}

            {/* Bill breakdown */}
            <div className="mt-4 pt-3 border-t space-y-1.5 text-[13px]" data-testid="order-bill">
              <div className="flex justify-between">
                <span className="text-muted-foreground inline-flex items-center gap-1.5"><Package className="w-3.5 h-3.5" />Item total</span>
                <span className="tabular-nums">{money(subtotal)}</span>
              </div>
              {tax > 0 && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground inline-flex items-center gap-1.5"><Receipt className="w-3.5 h-3.5" />GST &amp; taxes</span>
                  <span className="tabular-nums">{money(tax)}</span>
                </div>
              )}
              {discount > 0 && (
                <div className="flex justify-between text-emerald-700">
                  <span className="inline-flex items-center gap-1.5"><Tag className="w-3.5 h-3.5" />Coupon discount</span>
                  <span className="tabular-nums font-semibold">− {money(discount)}</span>
                </div>
              )}
              {wallet > 0 && (
                <div className="flex justify-between text-emerald-700">
                  <span className="inline-flex items-center gap-1.5"><Wallet className="w-3.5 h-3.5" />Wallet</span>
                  <span className="tabular-nums font-semibold">− {money(wallet)}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-muted-foreground inline-flex items-center gap-1.5"><Truck className="w-3.5 h-3.5" />Delivery fee</span>
                <span className="tabular-nums">
                  {deliveryFee === 0 ? <span className="text-emerald-700 font-semibold">FREE</span> : money(deliveryFee)}
                </span>
              </div>
              {packaging > 0 && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground inline-flex items-center gap-1.5"><Package className="w-3.5 h-3.5" />Packaging</span>
                  <span className="tabular-nums">{money(packaging)}</span>
                </div>
              )}
              <div className="flex justify-between text-lg md:text-xl font-display font-semibold pt-2 mt-2 border-t">
                <span>Total paid</span>
                <span className="tabular-nums" data-testid="order-total">{money(grand)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Right column — Delivery + Help */}
        <div className="space-y-4">
          {/* Delivery address card */}
          <Card className="border-border/60 rounded-2xl" data-testid="order-address-card">
            <CardContent className="p-4 md:p-5">
              <div className="font-display text-lg font-semibold mb-3 flex items-center gap-2">
                <MapPin className="w-4 h-4 text-primary" /> Delivery address
              </div>
              <div className="flex gap-3">
                <div className="w-9 h-9 rounded-lg bg-secondary text-foreground/70 grid place-items-center shrink-0"><Home className="w-4 h-4" /></div>
                <div className="text-[13px] leading-snug min-w-0">
                  <div className="font-semibold">{order.address?.name}</div>
                  <div className="text-muted-foreground">{order.address?.phone}</div>
                  <div className="text-muted-foreground">{[order.address?.line1, order.address?.line2, order.address?.landmark].filter(Boolean).join(", ")}</div>
                  <div className="text-muted-foreground">{order.address?.city}, {order.address?.state} — {order.address?.pincode}</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Sprint 58 — Delivery partner premium card. Appears once a rider is
              assigned. Shows name + phone + one-tap call. Nothing else is exposed. */}
          {(order.status === "ready" || order.status === "assigned" || order.status === "out_for_delivery" || order.delivery?.partner_id) && !isTerminal && order.status !== "delivered" && (
            <Card className="border-primary/20 rounded-2xl overflow-hidden shadow-sm" data-testid="order-partner-card">
              <div className="h-1 bg-gradient-to-r from-primary via-primary/70 to-primary" />
              <CardContent className="p-4 md:p-5">
                <div className="font-display text-lg font-semibold mb-3 flex items-center gap-2">
                  <Bike className="w-4 h-4 text-primary" /> Delivery partner
                </div>

                {order.delivery?.partner_id ? (
                  <>
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-primary/10 text-primary grid place-items-center shrink-0 font-bold text-lg ring-2 ring-primary/15">
                        {(order.delivery.partner_name || "R").slice(0, 1).toUpperCase()}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-[15px] font-semibold truncate flex items-center gap-2" data-testid="partner-name">
                          {order.delivery.partner_name || "Delivery partner"}
                          {order.delivery.partner_online && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 bg-emerald-100 rounded-full px-1.5 py-0.5">
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> Online
                            </span>
                          )}
                        </div>
                        {order.delivery.partner_phone && (
                          <div className="text-[12.5px] text-muted-foreground tabular-nums flex items-center gap-1 mt-0.5" data-testid="partner-phone">
                            <Phone className="w-3 h-3" /> {order.delivery.partner_phone}
                          </div>
                        )}
                        <div className="text-[11px] text-primary font-medium mt-0.5">
                          {order.delivery.status === "in_transit" ? "On the way to you" : "Preparing to head out"}
                        </div>
                      </div>
                    </div>

                    {order.delivery.partner_phone && (
                      <div className="mt-3 grid grid-cols-[1fr_auto] gap-2">
                        <Button asChild size="lg" className="h-12 btn-primary-glow font-bold tracking-wide" data-testid="partner-call-btn">
                          <a href={`tel:${order.delivery.partner_phone}`}>
                            <Phone className="w-4 h-4 mr-2" /> Call now
                          </a>
                        </Button>
                        <Button
                          variant="outline"
                          size="lg"
                          className="h-12 px-3"
                          data-testid="partner-copy-btn"
                          onClick={async () => {
                            try { await navigator.clipboard.writeText(order.delivery.partner_phone); toast.success("Phone number copied"); }
                            catch { toast.error("Copy failed"); }
                          }}
                          title="Copy number"
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="flex items-center gap-3 rounded-xl bg-secondary/50 border border-dashed border-border/60 px-3 py-3" data-testid="partner-pending">
                    <div className="w-10 h-10 rounded-full bg-primary/10 text-primary grid place-items-center shrink-0">
                      <Bike className="w-5 h-5 animate-pulse" />
                    </div>
                    <div className="text-[13px] leading-tight">
                      <div className="font-semibold">Delivery partner will be assigned soon</div>
                      <div className="text-[11px] text-muted-foreground">Your order is being packed. We'll update this once a rider is on the way.</div>
                    </div>
                  </div>
                )}

                {order.delivery?.otp && order.delivery?.partner_id && (
                  <div className="mt-3 rounded-xl border-2 border-dashed border-primary/30 bg-primary/[0.04] px-3 py-2.5 flex items-center justify-between" data-testid="order-otp">
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-primary font-bold">Share on delivery</div>
                      <div className="text-[11px] text-muted-foreground">Verify your rider with this OTP</div>
                    </div>
                    <div className="text-2xl font-display font-bold tracking-widest tabular-nums">{order.delivery.otp}</div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Payment card */}
          <Card className="border-border/60 rounded-2xl" data-testid="order-payment-card">
            <CardContent className="p-4 md:p-5 text-[13px] space-y-1.5">
              <div className="font-display text-lg font-semibold mb-2 flex items-center gap-2">
                <Receipt className="w-4 h-4 text-primary" /> Payment
              </div>
              <div className="flex justify-between"><span className="text-muted-foreground">Method</span><span className="uppercase font-semibold">{order.payment?.method}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Status</span><span className="capitalize font-semibold">{order.payment?.status}</span></div>
              {invoice && (
                <div className="mt-2 pt-2 border-t flex items-center gap-2 text-xs">
                  <ShieldCheck className="w-3.5 h-3.5 text-primary shrink-0" />
                  <span className="text-muted-foreground">Invoice</span>
                  <span className="font-mono font-semibold">{invoice.invoice_number}</span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Help section */}
          <Card className="border-border/60 rounded-2xl" data-testid="order-help-card">
            <CardContent className="p-4 md:p-5">
              <div className="font-display text-lg font-semibold mb-3 flex items-center gap-2">
                <LifeBuoy className="w-4 h-4 text-primary" /> Need help?
              </div>
              <div className="grid gap-2">
                <Button asChild variant="outline" className="justify-start h-10" data-testid="help-contact-store">
                  <a href={supportHref} target={whatsapp ? "_blank" : undefined} rel="noopener noreferrer">
                    {whatsapp ? <MessageCircle className="w-4 h-4 mr-2" /> : <Phone className="w-4 h-4 mr-2" />}
                    Contact {store?.name || "Babu Store"}
                  </a>
                </Button>
                {invoice && (
                  <Button variant="outline" className="justify-start h-10" onClick={() => window.print()} data-testid="help-download-invoice">
                    <Printer className="w-4 h-4 mr-2" /> Download / print invoice
                  </Button>
                )}
                {canCancel && (
                  <Button variant="outline" className="justify-start h-10 text-destructive hover:text-destructive" onClick={cancel} disabled={cancelling} data-testid="cancel-order-btn">
                    <AlertTriangle className="w-4 h-4 mr-2" /> {cancelling ? "Cancelling…" : "Cancel order"}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Sticky mobile support */}
      <div className="md:hidden fixed bottom-16 left-0 right-0 z-30 border-t bg-card/95 backdrop-blur px-3 py-2.5 flex items-center gap-2 slide-up-in" data-testid="order-mobile-support">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{status.label}</div>
          <div className="text-sm font-semibold truncate">Total {money(grand)}</div>
        </div>
        <Button asChild size="sm" className="h-10 btn-primary-glow" data-testid="order-mobile-support-btn">
          <a href={supportHref} target={whatsapp ? "_blank" : undefined} rel="noopener noreferrer">
            <LifeBuoy className="w-4 h-4 mr-1.5" /> Get help
          </a>
        </Button>
      </div>
    </div>
  );
}
